import { usePrefetchQuery, useQuery } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  AccountItem,
  cn,
  Permissions,
  queries,
  select,
  useAuthConfig,
  useAuthStore,
  useIsCurrentPermission,
  useWhoami,
} from "@theprelude/core";
import {
  Button,
  CheckmarkIcon,
  CloseIcon,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownItemIndicator,
  DropdownLabel,
  DropdownRadio,
  DropdownRadioGroup,
  DropdownSeparator,
  DropdownTrigger,
  IconButton,
  Input,
  MonitorIcon,
  PlusIcon,
  PreludeIcon,
  PreludeNameIcon,
  RotatingChevron,
  ScrollArea,
  ScrollAreaViewport,
  SearchIcon,
  SignalIcon,
  UserIcon,
} from "@theprelude/ds";
import React, { useMemo, useRef, useState } from "react";
import { ExceptionVisibilitySwitcher } from "@/components/control-monitor/exception-visibility-switcher";
import { Highlight } from "@/components/highlight";
import { useOverlay } from "@/hooks/use-overlay";
import { usePopper } from "@/hooks/use-popper";
import { tb, ts } from "@/lib/tracking";
import { Flag } from "../flag/flag";
import { ThemeSwitcher } from "../theme-switcher";
import { AlertsDropdown } from "./alerts-dropdown";

export default function AppHeader({
  children,
  title,
}: {
  title: string;
  children?: React.ReactNode;
}) {
  const isAdmin = useIsCurrentPermission(Permissions.ADMIN);
  const overlay = useOverlay();
  const canAddProbe = isAdmin.data;

  return (
    <header className="flex h-14 flex-wrap items-center justify-between bg-header-background pr-3 dark:border-stroke-line dark:border-b">
      <section className="flex items-center gap-3">
        <Link
          to="/home"
          className={cn(
            "flex cursor-pointer items-center gap-3 py-3 pl-3",
            tb("logo-home"),
          )}
        >
          <PreludeIcon className="h-6 w-8 fill-white xl:hidden" />
          <PreludeNameIcon className="hidden h-6 fill-white xl:block" />
        </Link>
        <svg
          width="13"
          height="33"
          viewBox="0 0 13 33"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="hidden xl:block"
        >
          <line
            x1="0.531482"
            y1="32.7549"
            x2="12.4055"
            y2="0.895755"
            stroke="rgb(var(--stroke-line))"
          />
        </svg>
        <HeaderNavigation title={title} />
      </section>

      <section className="flex-1">{children}</section>

      <section className="flex items-center gap-3">
        <Flag features={["policy_evaluator"]}>
          <ExceptionVisibilitySwitcher />
        </Flag>
        <ThemeSwitcher />
        {canAddProbe && (
          <Flag features={["detect"]}>
            <Button
              intent="pill"
              onClick={() => overlay.open("add-probe")}
              icon={<PlusIcon className="text-secondary-foreground" />}
              className={cn("gap-0 sm:gap-2", tb("add-a-probe"))}
            >
              <span className="hidden lg:block">Add a probe</span>
            </Button>
          </Flag>
        )}
        <Flag features={["policy_evaluator"]}>
          <AlertsDropdown />
        </Flag>
        <AccountDropdown />
      </section>
    </header>
  );
}

function AccountDropdown() {
  const whoami = useWhoami();
  const dropdown = usePopper();
  const authConfig = useAuthConfig();
  usePrefetchQuery(queries.iam.listAccounts(authConfig));

  return (
    <Dropdown {...dropdown}>
      <DropdownTrigger asChild disabled={whoami.isLoading}>
        <Button
          intent="pill"
          className={tb("user-dropdown-menu")}
          icon={<UserIcon className="text-secondary-foreground" />}
          data-testid="account-dropdown"
          extraIcon={
            <RotatingChevron active={dropdown.open} className={"ml-auto"} />
          }
        >
          <span className="hidden max-w-0 truncate sm:block sm:max-w-28 lg:max-w-56">
            {authConfig.handle}
          </span>
        </Button>
      </DropdownTrigger>

      <DropdownContent
        align="end"
        className="max-h-96 w-40 overflow-hidden sm:w-80"
      >
        <div className="flex items-center justify-between gap-1 p-2">
          <div className="flex flex-col justify-start truncate">
            <DropdownLabel>Handle</DropdownLabel>
            <span className="truncate leading-5">
              {authConfig.handle ?? "Unknown"}
            </span>
            <Link to="/account">
              <DropdownItem className="p-0 leading-5 underline hover:bg-transparent">
                Account settings
              </DropdownItem>
            </Link>
          </div>
          <Link to="/sign-out">
            <Button intent="secondary" className={tb("sign-out")}>
              Sign out
            </Button>
          </Link>
        </div>
        <AccountList />
      </DropdownContent>
    </Dropdown>
  );
}

const AccountItemHeight = 52;

function AccountList() {
  const authConfig = useAuthConfig();
  const { changeAccount } = useAuthStore(select("changeAccount"));
  const { data: accounts, isLoading } = useQuery(
    queries.iam.listAccounts(authConfig),
  );
  const router = useRouter();
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Filter and sort accounts
  const filteredAndSortedAccounts = useMemo(() => {
    const baseAccounts = accounts ?? [];

    const filtered =
      searchTerm.trim() === ""
        ? baseAccounts
        : baseAccounts.filter(
            (account) =>
              account.company
                .toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              account.account_id
                .toLowerCase()
                .includes(searchTerm.toLowerCase()),
          );

    return filtered.toSorted((a, b) => {
      if (a.account_id === authConfig.credentials?.account) return -1;
      if (b.account_id === authConfig.credentials?.account) return 1;
      return 0;
    });
  }, [accounts, searchTerm, authConfig.credentials?.account]);

  const rowVirtualizer = useVirtualizer({
    count: filteredAndSortedAccounts.length,
    estimateSize: () => AccountItemHeight,
    getScrollElement: () => scrollViewportRef.current,
    overscan: 5,
  });

  const scrollAreaHeight = Math.min(
    filteredAndSortedAccounts.length * AccountItemHeight,
    5 * AccountItemHeight,
  );

  return (
    <>
      <DropdownSeparator />
      <div className="flex items-center justify-between p-2">
        <DropdownLabel className="p-0">Accounts</DropdownLabel>
        {accounts && accounts.length >= 2 && (
          <IconButton
            onClick={() => {
              setShowSearch(!showSearch);
              setSearchTerm("");
            }}
            className={cn(tb("account-search-toggle"), "h-6 w-6")}
            icon={<SearchIcon className="h-3 w-3 text-secondary-foreground" />}
          />
        )}
      </div>
      {showSearch && (
        <div className="px-2 pb-2">
          <Input
            autoFocus
            type="text"
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            before={<SearchIcon className="text-secondary-foreground" />}
            after={
              <IconButton
                icon={<CloseIcon />}
                onClick={() => setSearchTerm("")}
              />
            }
            className="h-8 w-full"
          />
        </div>
      )}
      <DropdownSeparator />

      {isLoading && (
        <div className="flex h-full flex-col items-center justify-center gap-4 p-2">
          <div className="flex w-full flex-col gap-2">
            <div className="h-2 w-2/3 animate-pulse rounded-full bg-gray-200" />
            <div className="h-2 w-full animate-pulse rounded-full bg-gray-200" />
          </div>
          <div className="flex w-full flex-col gap-2">
            <div className="h-2 w-2/3 animate-pulse rounded-full bg-gray-200" />
            <div className="h-2 w-full animate-pulse rounded-full bg-gray-200" />
          </div>
        </div>
      )}

      {!isLoading && filteredAndSortedAccounts.length > 0 && (
        <DropdownRadioGroup value={authConfig.credentials?.account}>
          <ScrollArea style={{ height: `${scrollAreaHeight}px` }}>
            <ScrollAreaViewport ref={scrollViewportRef}>
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                  const account = filteredAndSortedAccounts[virtualItem.index];
                  if (!account) return null;

                  return (
                    <div
                      key={virtualItem.key}
                      data-index={virtualItem.index}
                      ref={rowVirtualizer.measureElement}
                      style={{
                        position: "absolute",
                        transform: `translateY(${virtualItem.start}px)`,
                        width: "100%",
                      }}
                    >
                      <Account
                        onClick={() => {
                          changeAccount(account.account_id);
                          void router.invalidate();
                        }}
                        account={account}
                        searchTerm={searchTerm}
                      />
                    </div>
                  );
                })}
              </div>
            </ScrollAreaViewport>
          </ScrollArea>
        </DropdownRadioGroup>
      )}
      {!isLoading && filteredAndSortedAccounts.length === 0 && (
        <div className="p-4 text-center text-secondary-foreground text-sm">
          No accounts found{searchTerm ? ` for "${searchTerm}"` : ""}.
        </div>
      )}
    </>
  );
}

const Account: React.FC<{
  onClick: () => void;
  account: AccountItem;
  searchTerm: string;
}> = ({ onClick, account, searchTerm }) => {
  return (
    <DropdownRadio
      value={account.account_id}
      onSelect={onClick}
      className="flex items-center justify-between"
    >
      <div className="flex flex-col justify-start truncate">
        <DropdownLabel>
          <Highlight text={account.account_id} highlight={searchTerm} />
        </DropdownLabel>
        <span className="font-medium text-sm">
          <Highlight text={account.company} highlight={searchTerm} />
        </span>
      </div>
      <DropdownItemIndicator>
        <CheckmarkIcon className="flex-shrink-0" />
      </DropdownItemIndicator>
    </DropdownRadio>
  );
};

const HeaderDropdownLabel: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return (
    <DropdownLabel className="group flex min-w-0 items-center justify-between border-t border-t-stroke-tertiary-line bg-gradient-to-b from-white to-stroke-secondary-line px-3 py-2 font-medium text-primary-foreground text-sm dark:from-[rgba(0,0,0,0.2)] dark:to-black">
      {children}
    </DropdownLabel>
  );
};

const HeaderDropdownRadio: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  return (
    <DropdownRadio
      className="group pl-6 hover:bg-secondary-background data-[state=checked]:bg-secondary-background data-[state=checked]:text-primary-foreground"
      value={title}
    >
      <div className="flex items-center gap-1">{children}</div>
    </DropdownRadio>
  );
};

const navItems = {
  monitor: {
    label: "Monitor",
    icon: (
      <MonitorIcon className="size-4 text-secondary-foreground group-[.selected]:text-primary-background dark:text-white dark:group-[.selected]:text-white" />
    ),
    items: [
      { label: "Control Monitor", href: "/control-monitor" },
      { label: "Executive Summary", href: "/executive-summary" },
    ],
  },
  detect: {
    label: "Detect",
    icon: (
      <SignalIcon className="size-4 text-secondary-foreground group-[.selected]:text-primary-background dark:text-white dark:group-[.selected]:text-white" />
    ),
    items: [
      { label: "Dashboard", href: "/dashboard" },
      { label: "Schedule", href: "/schedule" },
      { label: "Activity", href: "/activity" },
      { label: "Threat Intel", href: "/threat-intel" },
    ],
  },
};

function NavSection({
  params,
}: {
  params: {
    label: string;
    icon: React.ReactNode;
    items: { label: string; href: string }[];
  };
}) {
  const { label, icon, items } = params;
  return (
    <>
      <HeaderDropdownLabel>
        {label}
        {icon}
      </HeaderDropdownLabel>
      {items.map((item) => (
        <Link to={item.href} key={item.href}>
          <HeaderDropdownRadio title={item.label}>
            {item.label}
          </HeaderDropdownRadio>
        </Link>
      ))}
    </>
  );
}

const HeaderNavigation: React.FC<{ title: string }> = ({ title }) => {
  const dropdown = usePopper();

  return (
    <Dropdown {...dropdown}>
      <DropdownTrigger asChild>
        <Button
          data-testid="header-nav"
          className={cn(
            "w-full justify-between border-0 bg-transparent p-0 font-normal text-[21px] text-white hover:border-0 hover:bg-transparent hover:shadow-none",
            ts(title),
          )}
          icon={
            <RotatingChevron
              active={dropdown.open}
              className={"text-secondary-foreground"}
            />
          }
          iconPosition="right"
        >
          <span className="hidden xl:block">{title}</span>
        </Button>
      </DropdownTrigger>
      <DropdownContent className="w-60">
        <DropdownRadioGroup value={title}>
          <Flag features={["policy_evaluator"]}>
            <NavSection params={navItems.monitor} />
          </Flag>
          <Flag features={["detect"]}>
            <NavSection params={navItems.detect} />
          </Flag>
        </DropdownRadioGroup>
      </DropdownContent>
    </Dropdown>
  );
};
